import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyRupeeIcon,
  ShoppingCartIcon,
  UsersIcon,
  ShoppingBagIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { UserGroupIcon, ClipboardDocumentCheckIcon, ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/24/solid";
import { Package, TrendingUp, TrendingDown, Activity, Download, Plus, Cake, Heart } from 'lucide-react';

import {
  selectTodayAppointments,
  selectAllUsers,
  updateAppointmentStatus,
  fetchCRMUsers
} from '../../redux/slices/crmSlice';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import toast from 'react-hot-toast';
import { getAllTransactions, getTransactionSummary } from '../../services/transactionService';
import { getAllProducts } from '../../services/productService';
import { getAllUsers } from '../../services/userService';
import { getAllOrders } from '../../services/orderService';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { exportToExcel, exportToPDF, reportFormatters } from '../../utils/exportUtils';
import * as XLSX from 'xlsx';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const todayAppointments = useSelector(selectTodayAppointments);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30days');

  // Date picker states - Initialize with current date
  const today = format(new Date(), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  // Filter collapse state for mobile
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Data states
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // Analytics states
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalOrders: 0,
    todayOrders: 0,
    totalRevenue: 0,
    totalPurchases: 0,
    totalInwardVP: 0,
    totalOutwardVP: 0,
    vpBalance: 0,
    totalShakes: 0,
    avgOrderValue: 0,
    lowShakeUsers: 0,
  });

  const [orderAnalytics, setOrderAnalytics] = useState({
    byType: [],
    byPayment: [],
    topProducts: [],
    recentOrders: [],
  });

  const [userAnalytics, setUserAnalytics] = useState({
    growthData: [],
    byPurpose: [],
    newUsers: 0,
  });

  const [revenueAnalytics, setRevenueAnalytics] = useState({
    trend: [],
    byType: [],
    total: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, [dispatch, dateRange, startDate, endDate]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all data
      const [fetchedTransactions, fetchedProducts, fetchedUsers, fetchedOrders] = await Promise.all([
        getAllTransactions(),
        getAllProducts(),
        getAllUsers(),
        getAllOrders(),
      ]);

      // Filter data based on selected date range
      const filterStart = startOfDay(new Date(startDate));
      const filterEnd = endOfDay(new Date(endDate));

      const filteredOrders = fetchedOrders.filter(order => {
        const orderDate = new Date(order.timestamp || order.createdAt);
        return orderDate >= filterStart && orderDate <= filterEnd;
      });

      const filteredTransactions = fetchedTransactions.filter(transaction => {
        const transactionDate = new Date(transaction.timestamp || transaction.createdAt);
        return transactionDate >= filterStart && transactionDate <= filterEnd;
      });

      setUsers(fetchedUsers);
      setOrders(filteredOrders);
      setProducts(fetchedProducts);
      setTransactions(filteredTransactions);

      // Calculate analytics with filtered data
      calculateDashboardStats(fetchedUsers, filteredOrders, filteredTransactions);
      calculateOrderAnalytics(filteredOrders, fetchedProducts);
      calculateUserAnalytics(fetchedUsers);
      calculateRevenueAnalytics(filteredOrders);

      dispatch(fetchCRMUsers());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateDashboardStats = (users, orders, transactions) => {
    const now = new Date();
    const sevenDaysAgo = subDays(now, 7);
    const today = startOfDay(now);

    // Active users (users with orders in last 7 days)
    const activeUsers = users.filter(user => {
      const userOrders = orders.filter(o => o.userId === user.userId || o.userId === user.id);
      return userOrders.some(order => {
        const orderDate = new Date(order.timestamp || order.createdAt);
        return orderDate >= sevenDaysAgo;
      });
    }).length;

    // Today's orders
    const todayOrders = orders.filter(order => {
      const orderDate = new Date(order.timestamp || order.createdAt);
      return orderDate >= today && orderDate <= endOfDay(now);
    }).length;

    // Total revenue
    const totalRevenue = orders.reduce((sum, order) => {
      return sum + (order.summary?.grandTotalAmount || 0);
    }, 0);

    // Average order value
    const avgOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;

    // Total shakes in circulation
    const totalShakes = users.reduce((sum, user) => {
      return sum + (user.inventory?.remainingShakes || 0);
    }, 0);

    // Transaction Stats (Purchases & VP)
    const transactionSummary = transactions.reduce((acc, curr) => {
      if (curr.type === 'inward') {
        acc.totalPurchases += (curr.totalAmount || 0);
        acc.totalInwardVP += (curr.totalValuePoints || 0);
      } else if (curr.type === 'outward') {
        acc.totalOutwardVP += (curr.totalValuePoints || 0);
      }
      return acc;
    }, { totalPurchases: 0, totalInwardVP: 0, totalOutwardVP: 0 });

    const vpBalance = transactionSummary.totalInwardVP - transactionSummary.totalOutwardVP;

    // Users with low shake balance
    const lowShakeUsers = users.filter(user => {
      const shakes = user.inventory?.remainingShakes || 0;
      return shakes > 0 && shakes < 5;
    }).length;

    setDashboardStats({
      totalUsers: users.length,
      activeUsers,
      totalOrders: orders.length,
      todayOrders,
      totalRevenue,
      totalPurchases: transactionSummary.totalPurchases,
      totalInwardVP: transactionSummary.totalInwardVP,
      totalOutwardVP: transactionSummary.totalOutwardVP,
      vpBalance,
      totalShakes,
      avgOrderValue,
      lowShakeUsers,
    });
  };

  const calculateOrderAnalytics = (orders, products) => {
    // Orders by transaction type
    const typeCount = {};
    orders.forEach(order => {
      const type = order.transaction?.type || 'Unknown';
      typeCount[type] = (typeCount[type] || 0) + 1;
    });
    const byType = Object.entries(typeCount).map(([name, value]) => ({ name, value }));

    // Orders by payment method
    const paymentCount = {};
    orders.forEach(order => {
      const method = order.transaction?.paymentMethod || 'Unknown';
      paymentCount[method] = (paymentCount[method] || 0) + 1;
    });
    const byPayment = Object.entries(paymentCount).map(([name, value]) => ({ name, value }));

    // Top products
    const productCount = {};
    orders.forEach(order => {
      order.items?.forEach(item => {
        const name = item.productName;
        if (!productCount[name]) {
          productCount[name] = { name, quantity: 0, revenue: 0 };
        }
        productCount[name].quantity += item.quantity;
        productCount[name].revenue += item.totalPrice;
      });
    });
    const topProducts = Object.values(productCount)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    // Recent orders
    const recentOrders = [...orders]
      .sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt))
      .slice(0, 10);

    setOrderAnalytics({ byType, byPayment, topProducts, recentOrders });
  };

  const calculateUserAnalytics = (users) => {
    // User growth (last 30 days)
    const growthData = [];
    for (let i = 29; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const count = users.filter(user => {
        const createdDate = new Date(user.metadata?.createdAt || user.createdAt);
        return createdDate <= date;
      }).length;
      growthData.push({
        date: format(date, 'MMM dd'),
        users: count,
      });
    }

    // Users by purpose
    const purposeCount = {};
    users.forEach(user => {
      const purpose = user.accountSecurity?.detailedPurpose || 'Not Specified';
      purposeCount[purpose] = (purposeCount[purpose] || 0) + 1;
    });
    const byPurpose = Object.entries(purposeCount).map(([name, value]) => ({ name, value }));

    // New users (last 7 days)
    const sevenDaysAgo = subDays(new Date(), 7);
    const newUsers = users.filter(user => {
      const createdDate = new Date(user.metadata?.createdAt || user.createdAt);
      return createdDate >= sevenDaysAgo;
    }).length;

    setUserAnalytics({ growthData, byPurpose, newUsers });
  };

  const calculateRevenueAnalytics = (orders) => {
    // Revenue trend (last 30 days)
    const trend = [];
    for (let i = 29; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);

      const dayRevenue = orders
        .filter(order => {
          const orderDate = new Date(order.timestamp || order.createdAt);
          return orderDate >= dayStart && orderDate <= dayEnd;
        })
        .reduce((sum, order) => sum + (order.summary?.grandTotalAmount || 0), 0);

      trend.push({
        date: format(date, 'MMM dd'),
        revenue: dayRevenue,
      });
    }

    // Revenue by transaction type
    const typeRevenue = {};
    orders.forEach(order => {
      const type = order.transaction?.type || 'Unknown';
      typeRevenue[type] = (typeRevenue[type] || 0) + (order.summary?.grandTotalAmount || 0);
    });
    const byType = Object.entries(typeRevenue).map(([name, value]) => ({ name, value }));

    const total = orders.reduce((sum, order) => sum + (order.summary?.grandTotalAmount || 0), 0);

    setRevenueAnalytics({ trend, byType, total });
  };

  const getTodayCelebrations = () => {
    const now = new Date();
    const todayMonth = now.getMonth();
    const todayDate = now.getDate();
    const celebrations = [];

    users.forEach((user) => {
      const family = user.familyDetails || {};
      const userName = user.personalInformation?.fullName || user.personalDetails?.name || 'Unknown';
      const userId = user.userId || user.id;

      // Check user birthday
      if (family.birthday) {
        const bday = new Date(family.birthday);
        if (bday.getMonth() === todayMonth && bday.getDate() === todayDate) {
          celebrations.push({ type: 'birthday', name: userName, userId, label: 'Birthday' });
        }
      }

      // Check marriage anniversary
      if (family.marriageDate && family.maritalStatus === 'married') {
        const mday = new Date(family.marriageDate);
        if (mday.getMonth() === todayMonth && mday.getDate() === todayDate) {
          celebrations.push({ type: 'anniversary', name: userName, userId, label: 'Marriage Anniversary' });
        }
      }

      // Check children birthdays
      if (family.children?.length > 0) {
        family.children.forEach((child) => {
          if (child.birthday) {
            const cday = new Date(child.birthday);
            if (cday.getMonth() === todayMonth && cday.getDate() === todayDate) {
              celebrations.push({
                type: 'child_birthday',
                name: `${child.name} (${userName}'s ${child.gender})`,
                userId,
                label: 'Birthday',
              });
            }
          }
        });
      }
    });

    return celebrations;
  };

  const todayCelebrations = getTodayCelebrations();

  const handleStatusChange = async (userId, appointmentId, newStatus) => {
    try {
      await dispatch(updateAppointmentStatus({
        userId,
        appointmentId,
        status: newStatus
      })).unwrap();

      const statusText = newStatus === 'completed' ? 'completed' : 'cancelled';
      toast.success(`Appointment ${statusText} successfully!`);
    } catch (error) {
      console.error('Error updating appointment status:', error);
      toast.error('Failed to update appointment status');
    }
  };

  const handleExport = async (type, formatType = 'excel') => {
    let data = [];
    let fileName = '';
    let reportTitle = '';
    let head = [];
    let body = [];

    switch (type) {
      case 'users':
        data = reportFormatters.users(users);
        fileName = 'User_Report';
        reportTitle = 'Business Users Report';
        head = [['Name', 'Mobile', 'Email', 'Type', 'Purpose', 'Joined Date', 'Shakes', 'Referrer']];
        body = data.map(u => Object.values(u));
        break;
      case 'sales':
        // Enrich orders with user data
        const enrichedOrders = orders.map(order => {
          const user = users.find(u => u.id === order.userId || u.userId === order.userId);
          return {
            ...order,
            userName: user?.personalInformation?.fullName || user?.personalDetails?.name || user?.displayName || 'N/A',
            userMobile: user?.personalInformation?.mobileNumber || user?.personalDetails?.mobile || user?.mobileNumber || 'N/A',
          };
        });
        data = reportFormatters.orders(enrichedOrders);
        fileName = 'Sales_Report';
        reportTitle = 'Business Sales Report';
        head = [['Order ID', 'Date', 'User', 'Mobile', 'Type', 'Amount', 'VP', 'Shake Qty', 'Payment', 'Status']];
        body = data.map(o => Object.values(o));
        break;
      case 'inventory':
        data = reportFormatters.inventory(products);
        fileName = 'Inventory_Report';
        reportTitle = 'Business Inventory Report';
        head = [['Product', 'Code', 'Category', 'Stock', 'Price', 'VP', 'Status']];
        body = data.map(i => Object.values(i));
        break;
      case 'revenue':
        data = revenueAnalytics.trend.map(d => ({
          'Date': d.date,
          'Revenue': d.revenue
        }));
        fileName = 'Revenue_Report';
        reportTitle = 'Revenue Trend Report';
        head = [['Date', 'Revenue']];
        body = data.map(r => Object.values(r));
        break;
      default:
        toast.error('Invalid report type');
        return;
    }

    if (formatType === 'excel') {
      exportToExcel(data, fileName, type.toUpperCase());
    } else {
      exportToPDF({ title: reportTitle, head, body, fileName });
    }
    toast.success(`${fileName} generated successfully!`);
  };

  const exportData = () => {
    try {
      // Enrich orders with user data
      const enrichedOrders = orders.map(order => {
        const user = users.find(u => u.id === order.userId || u.userId === order.userId);
        return {
          ...order,
          userName: user?.personalInformation?.fullName || user?.personalDetails?.name || user?.displayName || 'N/A',
          userMobile: user?.personalInformation?.mobileNumber || user?.personalDetails?.mobile || user?.mobileNumber || 'N/A',
        };
      });

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();

      // Sheet 1: Dashboard Summary
      const summaryData = [
        { Metric: 'Total Users', Value: dashboardStats.totalUsers },
        { Metric: 'Active Users (7 days)', Value: dashboardStats.activeUsers },
        { Metric: 'Total Orders', Value: dashboardStats.totalOrders },
        { Metric: 'Today\'s Orders', Value: dashboardStats.todayOrders },
        { Metric: 'Total Revenue', Value: `₹${Math.round(dashboardStats.totalRevenue).toLocaleString()}` },
        { Metric: 'Average Order Value', Value: `₹${Math.round(dashboardStats.avgOrderValue).toLocaleString()}` },
        { Metric: 'Total Purchases', Value: `₹${Math.round(dashboardStats.totalPurchases).toLocaleString()}` },
        { Metric: 'Total Inward VP', Value: Math.round(dashboardStats.totalInwardVP) },
        { Metric: 'Total Outward VP', Value: Math.round(dashboardStats.totalOutwardVP) },
        { Metric: 'VP Balance', Value: Math.round(dashboardStats.vpBalance) },
        { Metric: 'Total Shakes in Inventory', Value: dashboardStats.totalShakes },
        { Metric: 'Users with Low Shake Balance', Value: dashboardStats.lowShakeUsers },
        { Metric: 'Report Date Range', Value: `${format(new Date(startDate), 'MMM dd, yyyy')} - ${format(new Date(endDate), 'MMM dd, yyyy')}` },
        { Metric: 'Generated On', Value: format(new Date(), 'MMM dd, yyyy HH:mm') },
      ];
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Dashboard Summary');

      // Sheet 2: Sales Report
      const salesData = reportFormatters.orders(enrichedOrders);
      const salesSheet = XLSX.utils.json_to_sheet(salesData);
      XLSX.utils.book_append_sheet(wb, salesSheet, 'Sales Report');

      // Sheet 3: Users Report
      const usersData = reportFormatters.users(users);
      const usersSheet = XLSX.utils.json_to_sheet(usersData);
      XLSX.utils.book_append_sheet(wb, usersSheet, 'Users Report');

      // Sheet 4: Inventory Report
      const inventoryData = reportFormatters.inventory(products);
      const inventorySheet = XLSX.utils.json_to_sheet(inventoryData);
      XLSX.utils.book_append_sheet(wb, inventorySheet, 'Inventory Report');

      // Sheet 5: Revenue Trend
      const revenueTrendData = revenueAnalytics.trend.map(d => ({
        'Date': d.date,
        'Revenue': d.revenue
      }));
      const revenueSheet = XLSX.utils.json_to_sheet(revenueTrendData);
      XLSX.utils.book_append_sheet(wb, revenueSheet, 'Revenue Trend');

      // Save the workbook
      XLSX.writeFile(wb, `Dashboard_Complete_Report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      toast.success('Complete dashboard report exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-slate-50 min-h-screen pb-52">
      {/* Header */}
      <div className="mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900">Dashboard</h2>
            <p className="text-[11px] md:text-sm text-gray-600 mt-1">
              Comprehensive overview of your business operations
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex gap-2">
              {/* <button
                onClick={exportData}
                className="flex items-center gap-2 px-3 md:px-4 h-9 md:h-10 bg-white border border-gray-300 hover:bg-gray-50 transition-all font-medium text-[11px] md:text-sm shadow-sm"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export All</span>
              </button> */}
              <button
                onClick={() => navigate('/admin/add-user')}
                className="flex-1 md:flex-none px-4 py-2 bg-slate-800 text-white transition-all shadow-lg shadow-slate-900/20 flex items-center justify-center gap-2 text-[11px] md:text-sm font-medium rounded-lg hover:bg-slate-700"
              >
                <Plus className="w-4 h-4" />
                <span>New User</span>
              </button>
              <button
                onClick={() => navigate('/recharge')}
                className="flex-1 md:flex-none px-4 py-2 bg-green-600 text-white transition-all shadow-lg shadow-green-600/20 flex items-center justify-center gap-2 text-[11px] md:text-sm font-medium rounded-lg hover:bg-green-700"
              >
                <Plus className="w-4 h-4" />
                <span>Recharge</span>
              </button>
            </div>
          </div>
        </div>

        {/* Date Range Filters - Collapsible on Mobile */}
        <div className="bg-white shadow-sm border border-slate-200 overflow-hidden rounded-xl mt-4">
          {/* Filter Header - Always Visible */}
          <button
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors md:cursor-default"
          >
            <div className="flex items-center gap-2">
              <CalendarDaysIcon className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter by Date</span>
              <span className="text-xs text-gray-500 hidden sm:inline">
                ({format(new Date(startDate), 'MMM dd')} - {format(new Date(endDate), 'MMM dd')})
              </span>
            </div>
            {/* Toggle icon - visible only on mobile */}
            <div className="md:hidden">
              {isFilterExpanded ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </button>

          {/* Filter Content - Collapsible on Mobile, Always Visible on Desktop */}
          <div
            className={`
              transition-all duration-300 ease-in-out overflow-hidden
              ${isFilterExpanded ? 'max-h-96 md:max-h-none' : 'max-h-0 md:max-h-none'}
            `}
          >
            <div className="px-4 pb-4 pt-2 border-t border-gray-100 md:border-t-0 md:pt-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label htmlFor="startDate" className="text-sm text-gray-600">From:</label>
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="endDate" className="text-sm text-gray-600">To:</label>
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      const today = format(new Date(), 'yyyy-MM-dd');
                      setStartDate(today);
                      setEndDate(today);
                    }}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      const today = new Date();
                      const lastWeek = subDays(today, 7);
                      setStartDate(format(lastWeek, 'yyyy-MM-dd'));
                      setEndDate(format(today, 'yyyy-MM-dd'));
                    }}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => {
                      const today = new Date();
                      const lastMonth = subDays(today, 30);
                      setStartDate(format(lastMonth, 'yyyy-MM-dd'));
                      setEndDate(format(today, 'yyyy-MM-dd'));
                    }}
                    className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium"
                  >
                    Last 30 Days
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
        {/* <KPICard
          title="Total Users"
          value={dashboardStats.totalUsers.toLocaleString()}
          subtitle={`${dashboardStats.activeUsers} active (7d)`}
          icon={UsersIcon}
          color="blue"
          trend="+12%"
          onExport={() => handleExport('users', 'excel')}
        /> */}
        <KPICard
          title="Total Orders"
          value={dashboardStats.totalOrders.toLocaleString()}
          subtitle={`${dashboardStats.todayOrders} today`}
          icon={ShoppingCartIcon}
          color="purple"
          trend="+8%"
          onExport={() => handleExport('sales', 'excel')}
        />
        <KPICard
          title="Revenue"
          value={`₹${Math.round(dashboardStats.totalRevenue).toLocaleString()}`}
          subtitle={`Avg: ₹${Math.round(dashboardStats.avgOrderValue)}`}
          icon={CurrencyRupeeIcon}
          color="green"
          trend="+15%"
          onExport={() => handleExport('revenue', 'excel')}
        />
        <KPICard
          title="Purchases"
          value={`₹${Math.round(dashboardStats.totalPurchases).toLocaleString()}`}
          subtitle={`${Math.round(dashboardStats.totalInwardVP).toLocaleString()} VP In`}
          icon={ShoppingBagIcon}
          color="orange"
          trend="Stock In"
          onExport={() => handleExport('inventory', 'excel')}
        />
        <KPICard
          title="VP Balance"
          value={Math.round(dashboardStats.vpBalance).toLocaleString()}
          subtitle={`In: ${Math.round(dashboardStats.totalInwardVP)} | Out: ${Math.round(dashboardStats.totalOutwardVP)}`}
          icon={ClipboardDocumentCheckIcon}
          color={dashboardStats.vpBalance >= 0 ? "indigo" : "red"}
          trend="Net VP"
          onExport={() => handleExport('inventory', 'excel')}
        />
        <KPICard
          title="Inventory"
          value={dashboardStats.totalShakes.toLocaleString()}
          subtitle={`${dashboardStats.lowShakeUsers} users low`}
          icon={Package}
          color="red"
          trend="-5%"
          trendDown
          onExport={() => handleExport('inventory', 'excel')}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6 animate-in fade-in slide-in-from-top-4 duration-700">
        {/* Left Column - Analytics */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {/* Revenue Trend */}
          <div className="bg-white shadow-sm border border-slate-200 p-6 rounded-xl">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-base font-semibold text-slate-800">Revenue Trend</h3>
                <p className="text-sm text-gray-500">Income over chosen period</p>
              </div>
              <div className="flex gap-2">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary/20 outline-none"
                >
                  <option value="7days">Last 7 Days</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="90days">Last 90 Days</option>
                </select>
                <button
                  onClick={() => handleExport('revenue', 'excel')}
                  className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                  title="Export Revenue Data"
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueAnalytics.trend}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#6B7280' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#6B7280' }}
                    tickFormatter={(value) => `₹${value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFF',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#10B981"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Order Analytics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Orders by Type */}
            {/* <div className="bg-white shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Orders by Type</h3>
                <button
                  onClick={() => handleExport('sales', 'excel')}
                  className="p-1 text-gray-400 hover:text-primary transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
              <div className="h-48 w-full mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orderAnalytics.byType}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {orderAnalytics.byType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div> */}

            {/* Top Products */}
            <div className="bg-white shadow-sm border border-slate-200 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-base font-semibold text-slate-800">Top Products</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Best performers by quantity</p>
                </div>
                <button
                  onClick={() => handleExport('inventory', 'excel')}
                  className="flex items-center gap-1.5 text-xs text-custom-500 hover:text-custom-700 font-medium transition-colors"
                  title="Export Product Report"
                >
                  View More
                  <Download className="w-3.5 h-3.5" />
                </button>
              </div>
              <ul className="space-y-3">
                {orderAnalytics.topProducts.map((product, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="flex items-center justify-center rounded-full bg-slate-100 size-10 flex-shrink-0">
                      <span className="text-slate-700 font-bold text-sm">#{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h6 className="text-sm font-semibold text-slate-800 truncate">{product.name}</h6>
                      <p className="text-xs text-slate-400">{product.quantity} units sold</p>
                    </div>
                    <span className="text-sm font-bold text-slate-800 whitespace-nowrap">
                      ₹{Math.round(product.revenue).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Recent Orders */}
          {/* <div className="bg-white shadow-sm border border-slate-200 p-6 rounded-xl">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-base font-semibold text-slate-800">Recent Transactions</h3>
                <p className="text-xs text-slate-400 mt-0.5">Latest orders in the selected period</p>
              </div>
              <button
                onClick={() => handleExport('sales', 'excel')}
                className="flex items-center gap-1.5 text-xs text-custom-500 hover:text-custom-700 font-medium transition-colors"
              >
                View Full Report
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-slate-100">
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Order</th>
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Customer</th>
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                    <th className="pb-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Amount</th>
                    <th className="pb-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {orderAnalytics.recentOrders.slice(0, 5).map((order, index) => (
                    <tr key={index} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3.5 whitespace-nowrap">
                        <p className="text-sm font-semibold text-slate-800">#{order.orderId?.substring(0, 8)}</p>
                        <p className="text-[10px] text-slate-400">{format(new Date(order.timestamp || order.createdAt), 'MMM dd, HH:mm')}</p>
                      </td>
                      <td className="py-3.5">
                        <p className="text-sm text-slate-700">{order.userName || 'N/A'}</p>
                      </td>
                      <td className="py-3.5">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${order.transaction?.type === 'RetailShake'
                          ? 'bg-orange-100 text-orange-600'
                          : order.transaction?.type === 'GuestShake'
                            ? 'bg-sky-100 text-sky-600'
                            : 'bg-purple-100 text-purple-600'
                          }`}>
                          {order.transaction?.type}
                        </span>
                      </td>
                      <td className="py-3.5 text-right">
                        <p className="text-sm font-bold text-slate-800">₹{Math.round(order.summary?.grandTotalAmount || 0).toLocaleString()}</p>
                      </td>
                      <td className="py-3.5 text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-600">
                          <CheckCircleIcon className="w-3 h-3" />
                          Done
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div> */}
        </div>

        {/* Right Column - Appointments & Quick Stats */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* Today's Celebrations */}
          {todayCelebrations.length > 0 && (
            <div className="bg-white shadow-sm border border-slate-200 overflow-hidden rounded-xl">
              <div className="bg-gradient-to-r from-pink-50 to-orange-50 px-4 py-3 border-b border-pink-100 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Cake className="h-5 w-5 text-pink-500" />
                    <div>
                      <h2 className="text-base font-semibold text-slate-800">Today's Celebrations</h2>
                      <p className="text-xs text-slate-400">{format(new Date(), 'MMMM d, yyyy')}</p>
                    </div>
                  </div>
                  <div className="bg-pink-500 px-2.5 py-1.5 rounded-xl">
                    <p className="text-lg font-bold text-white">{todayCelebrations.length}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 max-h-72 overflow-y-auto space-y-2">
                {todayCelebrations.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => navigate(`/admin/view-user/${item.userId}`)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${item.type === 'anniversary'
                      ? 'bg-pink-50 border border-pink-100 hover:bg-pink-100'
                      : 'bg-orange-50 border border-orange-100 hover:bg-orange-100'
                      }`}
                  >
                    {item.type === 'anniversary' ? (
                      <Heart className="w-5 h-5 text-pink-500 flex-shrink-0" />
                    ) : (
                      <Cake className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">{item.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Today's Appointments */}
          <div className="bg-white shadow-sm border border-slate-200 overflow-hidden rounded-xl">
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3 border-b border-slate-100 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon className="h-6 w-6 text-blue-500" />
                  <div>
                    <h2 className="text-base font-semibold text-slate-800">Today's Appointments</h2>
                    <p className="text-slate-400 text-xs">
                      {format(new Date(), 'EEEE, MMMM d')}
                    </p>
                  </div>
                </div>
                <div className="bg-blue-500 px-3 py-2 rounded-xl">
                  <p className="text-xl font-bold text-white">{todayAppointments.length}</p>
                </div>
              </div>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              {todayAppointments.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarDaysIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No appointments today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todayAppointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white shadow-sm border border-slate-200 p-6 rounded-xl">
            <div className="flex items-center mb-5">
              <h3 className="text-base font-semibold text-slate-800 grow">Quick Stats</h3>
            </div>
            <div>
              <QuickStat
                label="New Users (7d)"
                value={userAnalytics.newUsers}
                icon={TrendingUp}
                color="green"
              />
              <QuickStat
                label="Low Shake Users"
                value={dashboardStats.lowShakeUsers}
                icon={TrendingDown}
                color="red"
              />
              <QuickStat
                label="Avg Order Value"
                value={`₹${Math.round(dashboardStats.avgOrderValue)}`}
                icon={Activity}
                color="blue"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// KPI Card Component — Auxx-style solid colored background
const KPICard = ({ title, value, subtitle, icon: Icon, color, trend, trendDown, onExport }) => {
  const bgConfig = {
    blue: { card: 'bg-custom-500', sub: 'text-custom-200', icon: 'dark:bg-custom-500/20' },
    green: { card: 'bg-green-500', sub: 'text-green-200', icon: 'dark:bg-green-500/20' },
    purple: { card: 'bg-purple-500', sub: 'text-purple-200', icon: 'dark:bg-purple-500/20' },
    orange: { card: 'bg-orange-500', sub: 'text-orange-200', icon: 'dark:bg-orange-500/20' },
    red: { card: 'bg-red-500', sub: 'text-red-200', icon: 'dark:bg-red-500/20' },
    neutral: { card: 'bg-slate-700', sub: 'text-slate-300', icon: 'dark:bg-slate-700/20' },
    indigo: { card: 'bg-indigo-500', sub: 'text-indigo-200', icon: 'dark:bg-indigo-500/20' },
  };
  const cfg = bgConfig[color] || bgConfig.blue;

  return (
    <div
      onClick={onExport}
      className={`relative ${cfg.card} rounded-xl p-5 overflow-hidden cursor-pointer
        hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group
        before:absolute before:bg-white/10 before:size-16 before:rounded-tl-full before:bottom-0 before:right-0
        after:absolute after:bg-white/10 after:size-24 after:rounded-tl-full after:bottom-0 after:right-0`}
    >
      {/* Icon + Title */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`flex items-center justify-center text-white rounded-full bg-white/15 size-10 ${cfg.icon}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h6 className={`${cfg.sub} text-sm font-medium`}>{title}</h6>
      </div>
      {/* Value */}
      <h3 className="text-2xl font-bold text-white mb-3">{value}</h3>
      {/* Trend Badge */}
      {trend && (
        <span
          className={`px-2.5 py-0.5 text-xs inline-flex items-center gap-1 font-semibold rounded border
            bg-white border-white ${trendDown ? 'text-red-500' : 'text-green-500'
            }`}
        >
          {trendDown
            ? <TrendingDown className="w-3 h-3" />
            : <TrendingUp className="w-3 h-3" />}
          {trend}
        </span>
      )}
      {subtitle && !trend && (
        <p className={`text-xs ${cfg.sub} font-medium`}>{subtitle}</p>
      )}
      {subtitle && trend && (
        <p className={`text-xs ${cfg.sub} font-medium mt-1`}>{subtitle}</p>
      )}
      {/* Hover download icon */}
      {onExport && (
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <Download className="w-3.5 h-3.5 text-white/60" />
        </div>
      )}
    </div>
  );
};

// Quick Stat Component — Auxx style
const QuickStat = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  };
  const cls = colorClasses[color] || colorClasses.blue;

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${cls.bg}`}>
          <Icon className={`h-4 w-4 ${cls.text}`} />
        </div>
        <span className="text-sm text-slate-600 font-medium">{label}</span>
      </div>
      <span className={`text-sm font-bold ${cls.text}`}>{value}</span>
    </div>
  );
};

// Appointment Card Component
const AppointmentCard = ({ appointment, onStatusChange }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleComplete = async () => {
    setIsUpdating(true);
    await onStatusChange(appointment.userId, appointment.id, 'completed');
    setIsUpdating(false);
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      setIsUpdating(true);
      await onStatusChange(appointment.userId, appointment.id, 'cancelled');
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm text-slate-800 truncate">{appointment.title}</h4>
          <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
            <UserIcon className="h-3 w-3" />
            <span className="truncate">{appointment.userName}</span>
          </div>
        </div>
        <span className="text-xs font-semibold text-blue-500">
          {format(new Date(appointment.dateTime), 'HH:mm')}
        </span>
      </div>
      <div className="flex gap-2">
        <button
          onClick={handleComplete}
          disabled={isUpdating}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs transition-colors disabled:opacity-50"
        >
          <CheckCircleIcon className="h-3 w-3" />
          Done
        </button>
        <button
          onClick={handleCancel}
          disabled={isUpdating}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-colors disabled:opacity-50"
        >
          <XCircleIcon className="h-3 w-3" />
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;